import { useState, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	IconButton,
	Button,
	Stack,
	Divider,
	Avatar,
	Autocomplete,
	Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import dayjs from "dayjs";
import { StyledTextField, NormalButton } from "../../../components/ui/CommonReusables";
import { dialogCloseSx, primaryButtonSx, subtleIconSx, autocompleteSx } from "./buttonStyles";

const initialsOf = (name) =>
	name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0])
		.join("")
		.toUpperCase();

const ManageAgentBadgesDialog = ({ open, onClose, agent, allTrainings, onMarkAttended, onRemoveAttendance }) => {
	const [selected, setSelected] = useState(null);

	// Only trainings the agent has not attended can be added.
	const available = useMemo(() => {
		if (!agent) return [];

		const earnedIds = new Set(agent.badges.map((badge) => badge.trainingId));
		return allTrainings.filter((training) => !earnedIds.has(training.id));
	}, [agent, allTrainings]);

	if (!agent) return null;

	const handleMark = () => {
		if (!selected) return;

		onMarkAttended(selected.curriculumId, selected.id, { id: agent.id, name: agent.name });
		setSelected(null);
	};

	const handleClose = () => {
		setSelected(null);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						// Width stays margin-aware so the paper never overflows the viewport on small screens.
						m: { xs: 2, sm: 4 },
						width: { xs: "calc(100% - 32px) !important", sm: "calc(100% - 64px) !important" },
						maxWidth: "600px !important",
					},
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Avatar sx={{ width: 38, height: 38, bgcolor: "#EBF5FF", color: "primary.dark", fontWeight: 700, fontSize: 13 }}>
							{initialsOf(agent.name)}
						</Avatar>
						<Box>
							<Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>{agent.name}</Typography>
							<Typography sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
								{agent.badges.length} {agent.badges.length === 1 ? "training badge" : "training badges"}
							</Typography>
						</Box>
					</Stack>

					<IconButton onClick={handleClose} size="small" aria-label="Close manage badges dialog" sx={dialogCloseSx}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ width: "100%" }}>
				{/* Grant a badge */}
				<Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 1 }}>Mark as Attended</Typography>

				<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }}>
					<Autocomplete
						fullWidth
						size="small"
						options={available}
						value={selected}
						onChange={(_event, value) => setSelected(value)}
						getOptionLabel={(option) => option.title}
						groupBy={(option) => option.curriculumTitle}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						noOptionsText="Attended every training already"
						sx={autocompleteSx}
						renderInput={(params) => <StyledTextField {...params} placeholder="Select a training..." />}
					/>

					<NormalButton startIcon={<AddIcon />} onClick={handleMark} disabled={!selected} sx={{ flexShrink: 0, ...primaryButtonSx }}>
						Add Badge
					</NormalButton>
				</Stack>

				<Divider sx={{ my: 2.5 }} />

				{/* Badges earned */}
				<Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 1 }}>Trainings Attended ({agent.badges.length})</Typography>

				<Box sx={{ maxHeight: 320, overflowY: "auto" }}>
					{agent.badges.length === 0 ? (
						<Box sx={{ py: 4, textAlign: "center" }}>
							<Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>No trainings attended yet</Typography>
						</Box>
					) : (
						agent.badges.map((badge, index) => (
							<Box key={badge.trainingId}>
								<Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
									<WorkspacePremiumIcon sx={{ color: "#0F7A53", fontSize: 20, flexShrink: 0 }} />

									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography sx={{ fontSize: "0.825rem", fontWeight: 600 }} noWrap title={badge.trainingTitle}>
											{badge.trainingTitle}
										</Typography>
										<Typography sx={{ color: "text.secondary", fontSize: "0.7rem" }} noWrap component="div">
											{badge.curriculumTitle}
											{badge.attended_at ? ` · ${dayjs(badge.attended_at).format("MMM D, YYYY")}` : ""}
										</Typography>
									</Box>

									<Tooltip title="Remove badge">
										<IconButton
											size="small"
											onClick={() => onRemoveAttendance(badge.curriculumId, badge.trainingId, agent.id)}
											aria-label={`Remove ${badge.trainingTitle} badge`}
											sx={subtleIconSx}
										>
											<RemoveCircleOutlineIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Stack>

								{index < agent.badges.length - 1 && <Divider />}
							</Box>
						))
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={handleClose} sx={primaryButtonSx} variant="contained">
					Done
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ManageAgentBadgesDialog;
